import Button from '@ui-library/button';
import { H3 } from '@ui-library/typography';
import ConfirmChanges from '@ui-library/modal/confirmChanges';
import classes from '@components/sources/source-view/source-schema/sync-schema.module.scss';
import ConfiguredData, {
  FinalConfig,
} from '@components/sources/source/warehouseSource/configureData';
import { getConfigDataToUpdate } from '@components/sources/visualizer/mappingScreens/sourceUtil';
import VisualMapping, {
  SourceSettingsProps,
} from '@components/sources/visualizer/mappingScreens/visualMapping';
import useModalView from '@hooks/useModalView';
import { apiAuthCaller } from '@services/apiCaller';
import { IDestination } from '@stores/destination';
import { ISource, ISourceStore } from '@stores/source';
import { IUserStore } from '@stores/user';
import { IWorkspace } from '@stores/workspace';
import { Skeleton } from 'antd';
import { inject, observer } from 'mobx-react';
import React, {
  forwardRef,
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { IMessageStore } from '@stores/messages';
import { getApiErrorMessage } from '@components/common/util/util';
import { get } from 'lodash';
import {
  warehouseList,
  WAREHOUSE_DOC_LINKS,
} from '@components/sources/source/warehouseSource/warehouseSourceList/warehouseSettings';
import InfoPopover from '@ui-library/infoPopover';
import DocLinkLabel from '@components/sources/docLinksLabel';
import { ISourceDefinition } from '@stores/sourceDefinitionsList';
import { FixMe } from '@utils/types';
import useStores from '@stores/useStores';
import { ErrorProps } from './errorsView';
import { IField, IObject } from './visualDataMappingProvider';

interface StoreProps {
  workspaceStore?: IWorkspace;
  userStore?: IUserStore;
  messagesStore?: IMessageStore;
}
type WhActionMappingsValidateRespProp = { errorMessage?: string; sqlModelId?: string };
export type WhActionMappingsValidate = (
  skipValidation?: boolean,
  isUpdateConfig?: boolean,
  destinationId?: string,
) => Promise<WhActionMappingsValidateRespProp>;
export interface ISchemaEditProps extends StoreProps {
  mappingTypeValue?: string;
  source?: ISourceStore;
  destination: Partial<IDestination>;
  ref?: FixMe;
  setMessage?: (msg: string) => void;
  mappingSupportTypeVal?: string;
  setMappingTypeValue: (mappingTypeVal: string) => void;
  setMappingSupportTypeVal: (mappingTypeVal: string) => void;
  setBottomEditButtonVisible?: (show: boolean) => void;
  ifUpdate?: boolean;
  setValid: (isValid: boolean) => void;
  updateSchemaErrors: (errorItems: ErrorProps[]) => void;
}
export const mappingType: {
  calculating: string;
  oldMappingSelected: string;
  newMappingSelected: string;
} = {
  calculating: 'Calculate mapping type',
  oldMappingSelected: 'The user has selected old mapping',
  newMappingSelected: 'The user has selected new mapping',
};

export const mappingSupportType: {
  calculating: string;
  oldMappingSupport: string;
  newMappingSupport: string;
  updating: string;
} = {
  calculating: 'Calculate mapping support type',
  oldMappingSupport: 'The Reverse ETL and destination selection support no mapping',
  newMappingSupport: 'The Reverse ETL and destination selection does not support new mapping',
  updating: 'updating',
};
type SchemaEditProps = ISchemaEditProps;
// isMappedToDestination = true denotes the mapper used is visual data mapper.
export const isMappedToDestination = (constants: { key?: string; value?: string }[]) =>
  constants.find((curr) => curr?.key === 'context.mappedToDestination');

export const getMapperInfo = (sourceDef: ISourceDefinition, isVisualDataMapper?: boolean) => {
  const jsonMapperUseInstructionsLink = get(warehouseList, sourceDef.name.toUpperCase() || '', [
    {},
  ])[0]?.docLinks?.jsonMapperUseInstructions;

  if (!jsonMapperUseInstructionsLink) {
    return null;
  }
  const visualDataMapperLink = WAREHOUSE_DOC_LINKS.visualDataMapper;
  const popoverContent = !isVisualDataMapper ? (
    <DocLinkLabel
      insidePopover
      description="For the setup instructions through this JSON Mapper please click"
      linkRef={jsonMapperUseInstructionsLink}
    />
  ) : (
    <DocLinkLabel
      insidePopover
      description={`This Visual Data Mapper (VDM) makes it easy to map warehouse columns 
        from your ${sourceDef.displayName} instance to specific destination fields. For more info click`}
      linkRef={visualDataMapperLink}
    />
  );
  return <InfoPopover content={popoverContent} size="l" iconFontSize="24px" />;
};

const SchemaEditor: FunctionComponent<SchemaEditProps> = forwardRef(
  (props: SchemaEditProps, ref) => {
    const {
      mappingTypeValue,
      source,
      destination,
      ifUpdate,
      messagesStore,
      setMessage,
      setMappingTypeValue,
      mappingSupportTypeVal,
      setBottomEditButtonVisible,
      setMappingSupportTypeVal,
      setValid,
      workspaceStore,
      userStore,
      updateSchemaErrors,
    } = props;

    const { modelListStore } = useStores();

    const getInitialSourceConfig = (updatedConfig?: SourceSettingsProps | FinalConfig) => {
      if (mappingSupportTypeVal === mappingSupportType.updating) {
        return { ...source?.config, ...(updatedConfig || {}) } || {};
      }

      const sourceOldConfig = {
        schedule: source?.config?.schedule,
        accountId: source?.config?.accountId || source?.config?.rudderAccountId,
      };

      return { ...sourceOldConfig, ...(updatedConfig || {}) };
    };

    const createCustomField = async (
      object: IObject,
      selectedDestDefName: string,
      newFields: IField[],
      destinationId: string,
    ) => {
      if (!userStore?.token) {
        return false;
      }
      try {
        await apiAuthCaller(userStore?.token).post(
          `integrationsInfo/${selectedDestDefName}/customFields`,
          {
            object,
            fields: newFields,
            config: {
              destinationId,
              workspaceId: workspaceStore?.id,
            },
          },
        );
        return true;
      } catch (err) {
        messagesStore?.showErrorMessage(getApiErrorMessage(err, 'Field to create custom fields'));
        return false;
      }
    };
    const getDestinationId = (destinationId?: string) =>
      (destination.id || destinationId) as string;
    const updateSourceConfig = async (destinationId?: string) => {
      const mappedToDestination = isMappedToDestination(sourceSettings?.constants || []);
      let updateConfig;
      // Create custom fields only if the destination has been created.
      if (sourceSettings?.mappings && mappedToDestination) {
        const { newFields, object, ...restSourceConfig } = sourceSettings as SourceSettingsProps;
        updateConfig = restSourceConfig;
        const selectedDestDef = destination?.destinationDefinition;
        if (
          !!userStore?.token &&
          !!selectedDestDef?.name &&
          !!object?.supportsCustomFields &&
          newFields &&
          newFields?.length > 0 &&
          object?.name &&
          getDestinationId(destinationId)
        ) {
          const isCreateFieldSuccess = await createCustomField(
            object,
            selectedDestDef.name,
            newFields,
            getDestinationId(destinationId),
          );
          if (!isCreateFieldSuccess) {
            return 'Failed to create custom fields';
          }
        }
        if (destinationId) {
          return '';
        }
      } else if (sourceSettings?.mappings) {
        updateConfig = getConfigDataToUpdate(sourceSettings);
      }
      const isConfigUpdated = await source?.updateConfig(
        {
          isCredentialsValidationSupported: source.config.isCredentialsValidationSupported,
          ...updateConfig,
        },
        !ifUpdate,
      );
      return isConfigUpdated ? '' : 'Failed to update source configuration';
    };

    const [sourceSettings, setConfig] = useState<FixMe>(getInitialSourceConfig());

    const handleChange = useCallback(
      (updatedConfig: FixMe, isValid?: boolean) => {
        setConfig(getInitialSourceConfig(updatedConfig));
        if (setValid) {
          setValid(!!isValid);
        }
      },
      [setConfig],
    );

    if (ref) {
      React.useImperativeHandle(
        ref,
        /* whActionMappingsValidate will get called twice when connecting wh-action
      to a new destination and in the second run it'll run only createCustomField
      if mappedToDestination = true */
        () => ({
          async whActionMappingsValidate(
            skipValidation?: boolean,
            isUpdateConfig?: boolean,
            destinationId?: string,
          ): Promise<WhActionMappingsValidateRespProp> {
            const mappedToDestination = isMappedToDestination(sourceSettings?.constants || []);

            let errorMessage = '';
            if (!destinationId && !skipValidation && childRef?.current?.isValidConfiguredData) {
              errorMessage = childRef?.current?.isValidConfiguredData();
            }
            if (!errorMessage && isUpdateConfig && (!destinationId || mappedToDestination)) {
              errorMessage = await updateSourceConfig(destinationId);
            }

            return { errorMessage, sqlModelId: sourceSettings?.sqlModelId };
          },
        }),
      );
    }

    const childRef = useRef<FixMe>();

    const mappedToDestination = isMappedToDestination(sourceSettings?.constants || []);

    useEffect(() => {
      const getInteInfo = async () => {
        const ifDestSupportVDM = destination?.destinationDefinition?.config?.supportsVisualMapper;

        setMappingTypeValue(
          ifDestSupportVDM ? mappingType.newMappingSelected : mappingType.oldMappingSelected,
        );
        setMappingSupportTypeVal(
          ifDestSupportVDM
            ? mappingSupportType.newMappingSupport
            : mappingSupportType.oldMappingSupport,
        );
      };
      if (!ifUpdate) {
        getInteInfo();
      }
    }, [ifUpdate]);

    if (setMappingTypeValue && mappingSupportTypeVal === mappingSupportType.updating) {
      setMappingTypeValue(
        mappedToDestination ? mappingType.newMappingSelected : mappingType.oldMappingSelected,
      );
    }
    const switchMappings = () => {
      if (setMessage) {
        setMessage('');
      }
      updateSchemaErrors([]);
      if (setMappingTypeValue) {
        setMappingTypeValue(
          mappingTypeValue === mappingType.newMappingSelected
            ? mappingType.oldMappingSelected
            : mappingType.newMappingSelected,
        );
      }
      const { schedule, accountId, rudderAccountId } = source?.config as FixMe;
      const sourceOldConfig = { schedule, accountId: accountId || rudderAccountId };
      handleChange(sourceOldConfig);
    };
    const [cancelModal, showCancelModal] = useModalView(
      <ConfirmChanges
        onConfirm={() => {
          switchMappings();
          showCancelModal(false);
        }}
        onCancel={() => showCancelModal(false)}
        type="cancel"
        text="Switching will result in losing all the progress."
      />,
    );

    const defaultValue = {
      schema: sourceSettings?.query?.table?.schema,
      table: sourceSettings?.query?.table?.name,
      columns: sourceSettings?.query?.columns || sourceSettings?.columns,
      mapping: sourceSettings?.mappings,
      prefix: sourceSettings?.prefix,
      bucketName: sourceSettings?.bucketName,
      overridesConfig: sourceSettings?.overridesConfig,
      constants: sourceSettings?.constants,
      syncBehaviour: sourceSettings?.syncBehaviour,
      sqlModelId: sourceSettings?.sqlModelId || modelListStore.sourceModelMap[source?.id || ''],
    };
    const switchMappingsButtonText =
      mappingTypeValue === mappingType.newMappingSelected ? 'Map with JSON' : 'Map with Visualizer';
    const switchMappingsTooltipText =
      mappingSupportTypeVal === mappingSupportType.oldMappingSupport
        ? 'The selected destination does not support visual data mapping'
        : '';

    const updateConfig = (config: FixMe, isValid?: boolean) => {
      handleChange(config, isValid);
    };
    if (
      mappingSupportTypeVal !== mappingSupportType.calculating ||
      mappingSupportTypeVal === mappingSupportType.updating
    ) {
      return (
        <div className={classes.configure_data_wrapper}>
          {cancelModal}
          <div
            className={`${classes.configured_data_header} 
      ${
        mappingSupportTypeVal !== mappingSupportType.updating ? classes.create_connection_mode : ''
      }`}
          >
            <H3>
              Configured Data
              {source?.sourceDef &&
                getMapperInfo(
                  source.sourceDef,
                  mappingTypeValue === mappingType.newMappingSelected,
                )}
            </H3>
            {mappingSupportTypeVal !== mappingSupportType.updating && (
              <Button
                secondary
                disabled={mappingSupportTypeVal === mappingSupportType.oldMappingSupport}
                className={classes.switch__mapping_button}
                onClick={() => {
                  showCancelModal(true);
                }}
                name={switchMappingsButtonText}
                tooltip={switchMappingsTooltipText}
              />
            )}
          </div>
          {mappingTypeValue === mappingType.oldMappingSelected ||
          (mappingSupportTypeVal === mappingSupportType.updating && !mappedToDestination) ? (
            <ConfiguredData
              schedule={sourceSettings?.schedule}
              defaultValue={defaultValue}
              sourceDefinition={source?.sourceDef}
              accountId={sourceSettings?.accountId || sourceSettings?.rudderAccountId}
              onChange={updateConfig}
              viewOnly={false}
              ifUpdate={mappingSupportTypeVal === mappingSupportType.updating}
              updateSchemaErrors={updateSchemaErrors}
              destSyncBehaviourOptions={destination.destinationDefinition?.config?.syncBehaviours}
            />
          ) : (
            (mappingTypeValue === mappingType.newMappingSelected ||
              (mappingSupportTypeVal === mappingSupportType.updating && mappedToDestination)) && (
              <VisualMapping
                sourceSettings={{
                  ...sourceSettings,
                  sqlModelId: modelListStore.sourceModelMap[source?.id || ''],
                  accountId: sourceSettings.accountId || sourceSettings.rudderAccountId,
                }}
                source={source as ISource}
                destination={destination}
                ifUpdate={mappingSupportTypeVal === mappingSupportType.updating}
                ref={childRef}
                viewOnly={false}
                onChange={updateConfig}
                setBottomEditButtonVisible={setBottomEditButtonVisible}
                updateSchemaErrors={updateSchemaErrors}
              />
            )
          )}
        </div>
      );
    }
    return <Skeleton active paragraph={{ rows: 4 }} />;
  },
);
export default inject('userStore', 'workspaceStore', 'messagesStore')(observer(SchemaEditor));
